// AutoDeleteImages.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getDatabase,
  ref as dbRef,
  get,
  set,
  serverTimestamp,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  listAll,
  getMetadata,
  deleteObject,
} from "firebase/storage";

import {
  Stack,
  FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem,
  Button, Typography, LinearProgress
} from "@mui/material";
import AutoDeleteOutlinedIcon from "@mui/icons-material/AutoDeleteOutlined";

const TIMEFRAME_OPTIONS = [
  { value: "1week", label: "1 week", ms: 7 * 24 * 60 * 60 * 1000 },
  { value: "1month", label: "1 month", ms: 30 * 24 * 60 * 60 * 1000 },
  { value: "3months", label: "3 months", ms: 3 * 30 * 24 * 60 * 60 * 1000 },
  { value: "6months", label: "6 months", ms: 6 * 30 * 24 * 60 * 60 * 1000 },
];

const settingsPath = (watchId) => `Health/${watchId}/autoDeleteImages`;

const AutoDeleteImages = () => {
  const { watchId } = useParams();
  const [enabled, setEnabled] = useState(false);
  const [timeframe, setTimeframe] = useState("1month");
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState("");

  const timeframeMs = useMemo(
    () => TIMEFRAME_OPTIONS.find(t => t.value === timeframe)?.ms ?? TIMEFRAME_OPTIONS[1].ms,
    [timeframe]
  );

  const activeLabel = useMemo(
    () => TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.label ?? timeframe,
    [timeframe]
  );

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!watchId) return;
    try {
      const snap = await get(dbRef(getDatabase(), settingsPath(watchId)));
      if (snap.exists()) {
        const v = snap.val();
        if (typeof v?.enabled === "boolean") setEnabled(v.enabled);
        if (typeof v?.timeframe === "string") setTimeframe(v.timeframe);
      }
    } catch (e) {
      console.error("Load autoDeleteImages failed:", e);
    }
  }, [watchId]);

  // Save settings
  const saveSettings = useCallback(async () => {
    if (!watchId) return;
    try {
      await set(dbRef(getDatabase(), settingsPath(watchId)), {
        enabled, timeframe, updatedAt: serverTimestamp(),
      });
      alert("Auto-delete image settings saved.");
    } catch (e) {
      console.error("Save autoDeleteImages failed:", e);
      alert("Failed to save settings.");
    }
  }, [watchId, enabled, timeframe]);

  // Delete runner (charts/{watchId}/**)
  const runAutoDelete = useCallback(async () => {
    if (!watchId || !enabled) return;
    setBusy(true);
    try {
      const cutoff = Date.now() - timeframeMs;
      const storage = getStorage();
      const rootRef = storageRef(storage, `charts/${watchId}`);
      const result = await listAll(rootRef);

      let checked = 0;
      let deleted = 0;

      // Helper: safely get file timestamp from metadata or filename
      const getFileTs = async (itemRef) => {
        try {
          const meta = await getMetadata(itemRef);
          if (meta?.timeCreated) {
            const ts = Date.parse(meta.timeCreated);
            if (!Number.isNaN(ts)) return ts;
          }
        } catch {}
        // fallback: try parse from name like ...-2024-06-20T12:00:00Z.png
        const name = itemRef.name;
        const main = name.substring(0, name.lastIndexOf("."));
        const afterDash = main.substring(main.lastIndexOf("-") + 1);
        const ts = Date.parse(afterDash);
        return Number.isNaN(ts) ? 0 : ts;
      };

      // Delete top-level items
      for (const item of result.items) {
        const ts = await getFileTs(item);
        if (ts) {
          checked++;
          if (ts < cutoff) {
            await deleteObject(item);
            deleted++;
          }
        }
      }
      // If you have subfolders under charts/{watchId}, also traverse them:
      for (const folder of result.prefixes) {
        const sub = await listAll(folder);
        for (const item of sub.items) {
          const ts = await getFileTs(item);
          if (ts) {
            checked++;
            if (ts < cutoff) {
              await deleteObject(item);
              deleted++;
            }
          }
        }
      }

      setLastRun(`Checked ${checked} images, removed ${deleted} older than ${
        TIMEFRAME_OPTIONS.find(t => t.value === timeframe)?.label
      }.`);
      if (deleted > 0) alert(`${deleted} image${deleted > 1 ? "s" : ""} deleted.`);
    } catch (e) {
      console.error("Auto-delete images run failed:", e);
      alert("Auto-delete failed. See console for details.");
    } finally {
      setBusy(false);
    }
  }, [watchId, enabled, timeframe, timeframeMs]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { if (enabled) runAutoDelete(); }, [enabled, timeframe, runAutoDelete]);

  return (
    <section className="wg-card">
      <header className="wg-card__head">
        <span className="wg-icon-badge">
          <AutoDeleteOutlinedIcon />
        </span>
        <div className="wg-card__headings">
          <h2 className="wg-card__title">Auto-delete</h2>
          <div className="wg-card__sub">
            {enabled ? `Runs for images older than ${activeLabel}` : "Currently off"}
          </div>
        </div>
      </header>

      <div className="wg-card__body wg-card__body--tight">
        <FormControlLabel
          control={
            <Switch checked={enabled} onChange={() => setEnabled((v) => !v)} />
          }
          label="Delete old exports automatically"
        />

        <FormControl fullWidth size="small">
          <InputLabel>Keep images for</InputLabel>
          <Select
            value={timeframe}
            label="Keep images for"
            onChange={(e) => setTimeframe(e.target.value)}
            disabled={!enabled}
          >
            {TIMEFRAME_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={saveSettings} disabled={busy} fullWidth>
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={runAutoDelete}
            disabled={!enabled || busy}
            fullWidth
          >
            Run now
          </Button>
        </Stack>

        {busy && <LinearProgress />}
        {lastRun && (
          <Typography variant="body2" color="text.secondary">
            {lastRun}
          </Typography>
        )}
      </div>
    </section>
  );
};

export default AutoDeleteImages;
